'use client';
import React, { createContext, PropsWithChildren, useContext, useState } from 'react';

import { useEditedWorkflowActions } from '../../../contexts/editedWorkflows';
import { Workflow } from '@/app/workflows/actions/interfaces';
import { set, unset } from 'lodash';

import { NavigationContextProvider } from './NavigationContext';
import { VisibilityLevelContextProvider } from './VisibilityLevelContext';

interface PresetContextType {
  // Form Data Management
  formData: Record<string, unknown>;
  setFormData: (data: Record<string, unknown>) => void;
  removeFromFormData: (path: string) => Record<string, unknown>;
  addToFormData: (path: string, data: unknown) => void;

  // Workflow Management
  workflow: Workflow;
  getWorkflow: () => Workflow;
  clearChangesInForm: () => void;
  updateWorkflowAttributes: (key: string, value: Workflow[keyof Workflow]) => void;
}

export const PresetContext = createContext<PresetContextType>({
  // Form Data Management
  formData: {},
  setFormData: () => {},
  removeFromFormData: () => ({}),
  addToFormData: () => {},

  // Workflow Management
  workflow: {} as Workflow,
  getWorkflow: () => ({}) as Workflow,
  clearChangesInForm: () => {},
  updateWorkflowAttributes: () => {},
});

export const usePresetContext = () => {
  const data = useContext(PresetContext);
  if (!data) {
    throw new Error('useSchemaData must be used within a SchemaProvider');
  }
  return data;
};

export const PresetContextProvider = ({
  children,
  initialWorkflow,
}: PropsWithChildren<{
  initialWorkflow: Workflow;
}>) => {
  const [workflow, setWorkflow] = useState<Workflow>(initialWorkflow);

  const { upsertEditedWorkflow } = useEditedWorkflowActions();

  const resetWorkflow = () => setWorkflow(initialWorkflow);

  const clearChangesInForm = () => {
    resetWorkflow();
  };

  const updateWorkflowAttributes = (key: string, value: Workflow[keyof Workflow]) => {
    upsertEditedWorkflow(workflow.id);
    setWorkflow((prevWorkflow) => ({
      ...prevWorkflow,
      [key]: value,
    }));
  };

  const setFormData = (data: Record<string, unknown>) => {
    if (workflow.config === data) {
      return;
    }
    upsertEditedWorkflow(workflow.id);
    setWorkflow((prev) => ({ ...prev, config: data }));
  };

  const removePathFromFormData = (path: string): Record<string, unknown> => {
    const clonedFormData = JSON.parse(JSON.stringify(workflow.config));

    unset(clonedFormData, path);
    setFormData(clonedFormData);

    return clonedFormData;
  };

  const addToFormData = (path: string, data: unknown) => {
    const clonedFormData = JSON.parse(JSON.stringify(workflow.config));

    set(clonedFormData, path, data);

    setFormData(clonedFormData);
  };

  return (
    <PresetContext.Provider
      value={{
        // form data
        formData: workflow.config as Record<string, unknown>,
        setFormData,
        removeFromFormData: removePathFromFormData,
        addToFormData,

        // workflow
        workflow,
        getWorkflow: () => workflow,
        clearChangesInForm,
        updateWorkflowAttributes,
      }}
    >
      <NavigationContextProvider>
        <VisibilityLevelContextProvider>{children}</VisibilityLevelContextProvider>
      </NavigationContextProvider>
    </PresetContext.Provider>
  );
};
